/**
 * Companies: formations, orders, cohesion and veterancy.
 *
 * A company is the unit of command. The player never clicks an individual
 * soldier; they drag a company, point it, and watch twelve silhouettes take up
 * a shape. Formation slots are computed in company-local space and rotated onto
 * the field, which is what makes a line pivot instead of dissolving.
 */

import { MAP_H, MAP_W } from '../constants.ts';
import { alliedWith, areHostile, Team, Terrain, UF, UnitState, WallKind } from '../types.ts';
import { EV, LogTone, pushEvent, pushLog } from '../events.ts';
import {
  companyCommanded,
  findCompany,
  Formation,
  invalidateCompanyIndex,
  OrderKind,
  spawnUnit,
  Stance,
  type Company,
  type World,
} from '../world.ts';
import { unitType } from '../../data/units.ts';
import { race } from '../../data/races.ts';
import { techNode } from '../../data/tech.ts';
import { MANA_PER_THREAT } from './settlement.ts';
import { MOOT_KEEP } from './bindings.ts';
import { adultVillagers, musterAdultRefusal } from './demography.ts';
import { MUSTER_GRACE_HEADS } from '../../data/demography.ts';
import { canAfford, RESOURCE_KEYS, spend } from '../types.ts';
import { armies, type BattleArmy } from '../battle.ts';
import { beginCrossingRoutes, crossesWater, findCrossingPath, formationLineClear, gateTransitTarget } from './gatepath.ts';
import { steerOverGround } from '../field.ts';
import { clamp, rotateToward, wrapAngle } from '../../core/math.ts';

export const RANK_NAMES = ['Raw', 'Blooded', 'Veteran', 'Ironbound', 'Storied'];
const RANK_XP = [0, 140, 380, 820, 1600];

const COMPANY_NAMES = [
  'the Sixth',
  'the Grey Ninth',
  'Widow’s Own',
  'the Bell Company',
  'Ash and Iron',
  'the Long Watch',
  'Carrion Company',
  'the Gutter Guard',
  'the Ninth Bell',
  'Thornwake',
  'the Cold Levy',
  'Barrow Company',
  'the Unburied',
  'Sable Rank',
  'the Late Muster',
  'Hollow Company',
];

/** Scratch for the anchor's flow-field sample; the tick is single-threaded. */
const anchorFlow = { x: 0, y: 0 };
/** Scratch for `steerOverGround`, which runs per company per tick. */
const groundSteer = { x: 0, y: 0 };
/** Scratch for the per-model formation slot, for the same reason. */
const slotScratch = { x: 0, y: 0 };
const gateScratch = { x: 0, y: 0 };

/** How close an advancing company gets before it stops pressing forward. */
const CONTACT = 1.6;

/** How far a banner may get ahead of the men carrying it, in tiles. */
const LEASH = 5.5;

/** Ticks between re-layings of a marching company's route. */
const ROUTE_REFRESH = 150;

/**
 * Never let the banner outrun the men.
 *
 * Without it the anchor sails on while the company is held up at a wall, the
 * formation slots stream off into the settlement, and the men chase a line that
 * is no longer where they are fighting. Taken against a centroid the caller has
 * already worked out, because the uncommanded branch needs it for other reasons
 * and computing it twice would be a second walk of the roster.
 */
function leashTo(c: Company, cxm: number, cym: number): void {
  const lead = Math.hypot(c.ax - cxm, c.ay - cym);
  if (lead <= LEASH) return;
  const k = LEASH / lead;
  c.ax = cxm + (c.ax - cxm) * k;
  c.ay = cym + (c.ay - cym) * k;
}

/** The same leash, for a caller that has not already summed the roster. */
function leashAnchor(w: World, c: Company): void {
  const u = w.units;
  let sx = 0;
  let sy = 0;
  let n = 0;
  for (const h of c.members) {
    const i = u.resolve(h);
    if (i < 0) continue;
    sx += u.x[i];
    sy += u.y[i];
    n++;
  }
  if (n === 0) return;
  leashTo(c, sx / n, sy / n);
}

/**
 * The nearest company of any other team, by anchor.
 *
 * This was a scan of `w.companies` per company, which is fine at the dozen a
 * skirmish fields and quadratic at the two and a half thousand a
 * thirty-thousand-model battle fields — six million anchor comparisons a tick,
 * and the single most expensive thing on the battle profile. The anchors go
 * into a uniform grid once per tick instead and each company searches outward
 * from its own, which is the same structure as everything else that has to
 * answer "nearest" at this scale.
 *
 * The tie-break moves from "first in company order" to "first in grid order
 * among the equidistant". Both are pure functions of world state — this feeds
 * the anchor, which is hashed, so what matters is that nothing here depends on
 * iteration luck, and nothing does.
 */
const ANCHOR_CELL = 16;
const ANCHOR_CELLS_X = Math.ceil(MAP_W / ANCHOR_CELL);
const ANCHOR_CELLS_Y = Math.ceil(MAP_H / ANCHOR_CELL);

class AnchorGrid {
  private readonly cells: number[][] = Array.from(
    { length: ANCHOR_CELLS_X * ANCHOR_CELLS_Y },
    () => [],
  );
  private readonly touched: number[] = [];
  private live = 0;

  build(w: World): void {
    for (const c of this.touched) this.cells[c].length = 0;
    this.touched.length = 0;
    this.live = 0;
    for (let n = 0; n < w.companies.length; n++) {
      const o = w.companies[n];
      if (o.dead || o.members.length === 0) continue;
      const cell = anchorCell(o.ax, o.ay);
      if (this.cells[cell].length === 0) this.touched.push(cell);
      this.cells[cell].push(n);
      this.live++;
    }
  }

  nearestEnemy(w: World, self: Company): Company | null {
    if (this.live === 0) return null;
    const cx0 = clampAnchor(Math.floor(self.ax / ANCHOR_CELL), ANCHOR_CELLS_X);
    const cy0 = clampAnchor(Math.floor(self.ay / ANCHOR_CELL), ANCHOR_CELLS_Y);
    const maxRing = Math.max(ANCHOR_CELLS_X, ANCHOR_CELLS_Y);
    let best: Company | null = null;
    let bestD = Infinity;

    for (let ring = 0; ring <= maxRing; ring++) {
      const xLo = cx0 - ring;
      const xHi = cx0 + ring;
      const yLo = cy0 - ring;
      const yHi = cy0 + ring;
      for (let cy = yLo; cy <= yHi; cy++) {
        if (cy < 0 || cy >= ANCHOR_CELLS_Y) continue;
        const onYEdge = cy === yLo || cy === yHi;
        for (let cx = xLo; cx <= xHi; cx++) {
          if (cx < 0 || cx >= ANCHOR_CELLS_X) continue;
          if (!onYEdge && cx !== xLo && cx !== xHi) continue;
          const bucket = this.cells[cy * ANCHOR_CELLS_X + cx];
          for (let n = 0; n < bucket.length; n++) {
            const o = w.companies[bucket[n]];
            if (o === undefined || !areHostile(self.team, o.team) || o.dead || o.members.length === 0) continue;
            const dx = o.ax - self.ax;
            const dy = o.ay - self.ay;
            const d = dx * dx + dy * dy;
            if (d < bestD) {
              bestD = d;
              best = o;
            }
          }
        }
      }
      if (best !== null && bestD <= (ring * ANCHOR_CELL) * (ring * ANCHOR_CELL)) break;
    }
    return best;
  }
}

function anchorCell(x: number, y: number): number {
  return (
    clampAnchor(Math.floor(y / ANCHOR_CELL), ANCHOR_CELLS_Y) * ANCHOR_CELLS_X +
    clampAnchor(Math.floor(x / ANCHOR_CELL), ANCHOR_CELLS_X)
  );
}

function clampAnchor(v: number, span: number): number {
  return v < 0 ? 0 : v >= span ? span - 1 : v;
}

const anchorGrid = new AnchorGrid();

/**
 * The enemy company in this company's own lane, battle mode only.
 *
 * The armies deploy facing each other across y, so a lane is an x — lateral
 * offset is weighted well above forward distance, and a company only wheels
 * inward once nothing stands in front of it. The foe list is the opposing
 * army's own roster (a couple of dozen at most), so a linear scan per company
 * per tick costs less than the grid query it replaces.
 */
function lanedFoe(w: World, self: Company, sides: readonly BattleArmy[] | null): Company | null {
  if (sides === null) return null;
  let best: Company | null = null;
  let bestScore = Infinity;
  /**
   * Every banner this company is at war with, not "the other one of the two".
   * In a 1v1 that is the same list it always was; in a 2v2 a company on the
   * seam can genuinely wheel onto either of the two in front of it.
   *
   * Walked army by army rather than through `hostileCompanyIds`, which would
   * allocate a fresh array per company per tick — this runs for every company
   * on the field, and a battle fields thousands.
   */
  for (const a of sides) {
    if (!areHostile(self.team, a.team)) continue;
    for (const id of a.companies) {
      const f = findCompany(w, id);
      if (!f || f.dead || f.members.length === 0) continue;
      const score = Math.abs(f.ax - self.ax) * 2.4 + Math.abs(f.ay - self.ay);
      if (score < bestScore) {
        bestScore = score;
        best = f;
      }
    }
  }
  return best;
}

/**
 * Does somebody give this company its orders, or does it steer itself?
 *
 * The player's own banner always. On a shared field both sides are people, so
 * both are commanded. **A 2v2 ally is deliberately not**: it is a second crown
 * with an army of its own, and a partner you could drag around the field would
 * be a second army rather than an ally. It steers itself exactly as the enemy
 * always has, which is also why the 2v2 needed no new AI.
 */
function commanded(w: World, c: Company): boolean {
  return companyCommanded(w, c);
}

/**
 * A pure function of the run's own state.
 *
 * This used to advance a module-level cursor, which made a company's name
 * depend on how many companies every *other* world in the process had already
 * raised — so the second run in a session, a replay, or the harness's fifth
 * seed all named their companies differently from the run they were meant to
 * reproduce. `hashWorld` does not cover `name`, so nothing caught it. Seeding
 * off the run's own seed keeps the names varied between runs and identical
 * within one.
 */
export function nextCompanyName(w: World): string {
  const salt = (w.config.seed >>> 3) % COMPANY_NAMES.length;
  return COMPANY_NAMES[(w.nextCompanyId + salt) % COMPANY_NAMES.length];
}

export function createCompany(w: World, typeIdx: number, team: number, x: number, y: number): Company {
  const t = unitType(typeIdx);
  const c: Company = {
    id: w.nextCompanyId++,
    typeIdx,
    team,
    name: team === Team.Player ? nextCompanyName(w) : t.name,
    members: [],
    maxSize: t.companySize,
    formation: (t.flags & UF.Ranged) !== 0 ? Formation.Loose : Formation.Line,
    formationWidth: 0,
    stance: Stance.Guard,
    ax: x,
    ay: y,
    facing: 0,
    orderX: x,
    orderY: y,
    orderKind: OrderKind.None,
    orderFacing: null,
    route: [],
    focusCompany: -1,
    xp: 0,
    rank: 0,
    kills: 0,
    losses: 0,
    dead: false,
    lastLossTick: -999,
    cohesion: 1,
    flowKind: 0,
    gate: -1,
    garrisonTile: -1,
    boss: false,
    usedSecondWind: false,
  };
  w.companies.push(c);
  invalidateCompanyIndex(w);
  return c;
}

/** Fill a company up to `count` members, spawning around (x, y). */
export function fillCompany(w: World, c: Company, count: number, x: number, y: number): number {
  const t = unitType(c.typeIdx);
  let made = 0;
  // Spawn each model *on* the slot it is about to be told to stand in, rotated
  // the same way `stepCompanies` rotates it. The old form swapped the axes and
  // halved them, so every model started well outside its slot — which in battle
  // mode dropped the whole enemy army onto the long-range navigation branch and
  // marched it at the player before the horns had sounded.
  const cos = Math.cos(c.facing);
  const sin = Math.sin(c.facing);
  for (let n = 0; n < count; n++) {
    if (c.members.length >= c.maxSize) break;
    const off = slotOffset(c.formation, c.members.length, c.maxSize, spacingFor(t.radius), c.formationWidth);
    const i = spawnUnit(w, c.typeIdx, c.team, x + off.x * cos - off.y * sin, y + off.x * sin + off.y * cos);
    if (i < 0) break;
    w.units.companyId[i] = c.id;
    w.units.slotIdx[i] = c.members.length;
    c.members.push(w.units.handle(i));
    made++;
  }
  return made;
}

/**
 * Whether the muster will fit, whatever this people is bounded by.
 *
 * The one place in the game where `RaceDef.binding` is actually read, and it is
 * deliberately one place: the binding is a *fork in a single question* — "have I
 * room for this company" — rather than a family of parallel systems. Adding a
 * binding is a case here, and a race whose binding is not yet built falls
 * through to housing, which is the honest failure mode: it plays as everybody
 * else rather than being unplayable.
 *
 * Exported because the muster panel must be able to grey a button and say why,
 * and because the difference between "no room" and "no coin" is the whole of
 * what a player needs told.
 *
 * `why` is set **only when the muster is actually refused**, and that is a
 * deliberate narrowing now that a company is raised at whatever strength the
 * town can manage (see `musterStrength`). A short muster is not a refusal and
 * must not be printed as one: the People ledger prints this sentence followed
 * by "the next company waits on the cradle", which would be a lie about a
 * company that is about to march at eleven strong.
 */
export function musterRoom(w: World, typeIdx: number, size: number): { ok: boolean; why: string } {
  const s = musterStrength(w, typeIdx, size);
  const ok = s.n >= minMuster(size);
  return { ok, why: ok ? '' : s.why };
}

/**
 * The smallest fraction of an establishment worth putting a banner over.
 *
 * A company is raised at whatever strength the town can pay for and house — see
 * `musterStrength` — and this is the floor under that. A quarter, so that a
 * forty-man line can be started by a town with ten spare beds, and so that
 * "raise a one-man company and reinforce it" is not a way to buy a regiment on
 * the instalment plan.
 */
export const MIN_MUSTER_SHARE = 0.25;

export function minMuster(size: number): number {
  return Math.max(1, Math.min(size, Math.ceil(size * MIN_MUSTER_SHARE)));
}

/**
 * How many men this people can actually put on the roll today, and what stopped
 * it being the whole establishment.
 *
 * **The muster takes what it can get.** This is the change that pays for the
 * establishments getting three times bigger: a company is an *organisation*
 * with a roll of forty, not a purchase of forty men, and a town with fourteen
 * spare beds raises the Sixth fourteen strong and fills it as it grows. Without
 * it a settlement could not raise its first company until it had built three
 * houses and saved four hundred timber — the opening ten waves of the game,
 * deleted by a data change.
 *
 * It also keeps the promise the price already made: costs in `units.ts` are per
 * *model*, so a fourteen-strong muster costs fourteen men's worth. A given purse
 * buys the same number of soldiers it always did.
 *
 * Every binding answers the same question here, in the same shape, and the
 * refusal strings are the ones the panel has always printed — `musterRoom` is
 * this function asking "is it worth raising at all".
 */
export function musterStrength(w: World, typeIdx: number, size: number): { n: number; why: string } {
  const t = unitType(typeIdx);
  /**
   * The Academy's gate, before any binding's. A company whose art has not been
   * learned is refused with the node's own name, so the refusal is a signpost
   * to the study rather than a shrug — and it is checked here because this is
   * the one door every muster comes through: the player's command, the
   * campaign's levies and every debug path alike.
   */
  if (t.tech && !w.techNodes.includes(t.tech)) {
    const node = techNode(t.tech);
    return { n: 0, why: `Wants ${node?.name ?? t.tech}, which has not been studied.` };
  }
  switch (race(w.raceId).binding) {
    case 'mana': {
      const per = Math.max(0.0001, t.threat * MANA_PER_THREAT);
      const n = Math.min(size, Math.floor((w.manaMax - w.mana) / per));
      return { n, why: n >= size ? '' : 'The pool will not hold them. Widen it, or hold fewer.' };
    }
    /**
     * The Gilded Vigil. Names, and *only* names — the bed test below is not
     * reached at all, because the passive says the Vigil is bounded by
     * consecrated names and not by housing, and a binding that is the housing
     * rule with a second gate bolted on is not a different shape, it is the same
     * shape and stricter.
     */
    case 'names':
      return w.consecratedNames >= 1
        ? { n: size, why: '' }
        : {
            n: 0,
            why: 'No name is ready. The Vigil fields nothing it cannot call by name — the scriptorium is working.',
          };
    /**
     * The Drowned Court. A company is carried off rather than raised, one thrall
     * to a man, and the pens are the whole of the constraint: a Court with a
     * treasury and no captives cannot field anything at all.
     */
    case 'thralls':
      return {
        n: Math.min(size, Math.floor(w.thralls)),
        why:
          w.thralls >= size
            ? ''
            : `You hold ${Math.floor(w.thralls)} of the ${size} this company wants. Go and take the rest; nobody here volunteers.`,
      };
    /**
     * The Splitmoot, and this is the arm that is interesting for what it does
     * *not* do: there is no capacity test anywhere in it. The warren has no
     * ceiling and the town's tolerance refuses nothing — it merely turns, in
     * `stepPress`. The only refusal is that a company is *released* from the
     * warren rather than recruited into the town, so there has to be a warren to
     * release it from.
     */
    case 'moot': {
      const spare = w.counts.villagers - MOOT_KEEP;
      return {
        n: Math.max(0, Math.min(size, spare)),
        why: spare >= size ? '' : 'The warren has nobody spare. Wait — there is always more of it shortly.',
      };
    }
    default:
      break;
  }
  /**
   * Only adults muster. Reached by exactly the bindings that raise companies
   * out of the living town — housing, `irreplaceable` and the unbuilt `crews`
   * fall through to here; `mana`, `names`, `thralls` and `moot` returned
   * above, and each carries its own written exemption in
   * `musterAdultRefusal`'s comment. Before the bed test, because "you have
   * the beds and not the people" and "you have the people and not the beds"
   * are different sentences and the player should get the right one.
   *
   * `MUSTER_GRACE_HEADS` is counted as heads here rather than as slack on a
   * comparison, which is the same number doing the same job: the old rule
   * refused a muster of `size` when `size > grown + grace`, and this one lets a
   * muster of `size` come in at `grown + grace` men. A town of ten grown hands
   * still fields the fourteen it always did, and a town of none still fields
   * nothing worth a banner.
   */
  const hands = adultVillagers(w) + MUSTER_GRACE_HEADS;
  const beds = Math.floor((w.popCap + 4 - w.pop) / Math.max(0.0001, t.pop));
  const n = Math.max(0, Math.min(size, hands, beds));
  if (n >= size) return { n, why: '' };
  /**
   * Which sentence, when both bite. The adult gate speaks first — deliberately,
   * and this is the order the old all-or-nothing test was written in: "you have
   * the beds and not the people" and "you have the people and not the beds" are
   * different problems with different answers, and the People ledger compares
   * against `musterAdultRefusal`'s own wording to decide whether to print it.
   */
  const grown = musterAdultRefusal(w, size);
  if (grown !== '') return { n, why: grown };
  return { n, why: beds < size ? 'There is nowhere for them to sleep.' : '' };
}

/**
 * How many models of this type the treasury will bear.
 *
 * The purse is asked in models rather than in companies because that is what a
 * price in `units.ts` is denominated in. A town that can pay for eleven
 * militiamen raises eleven militiamen; before this pass it raised nothing at
 * all, because the muster was a single indivisible purchase of the whole
 * establishment.
 */
export function affordableModels(w: World, cost: Record<string, number | undefined>, cap: number): number {
  let n = cap;
  for (const k of RESOURCE_KEYS) {
    const per = cost[k] ?? 0;
    if (per <= 0) continue;
    n = Math.min(n, Math.floor(w.res[k] / per));
  }
  return Math.max(0, n);
}

export function recruitCompany(w: World, typeIdx: number, x: number, y: number): Company | null {
  const t = unitType(typeIdx);
  const est = t.companySize;
  /**
   * Raised at whatever strength the town can pay for and house, down to a
   * quarter of the roll. The company keeps its full establishment as `maxSize`,
   * so the gap is a gap the player can see and reinforce into rather than a
   * smaller company pretending to be whole.
   */
  const room = musterStrength(w, typeIdx, est);
  const n = Math.min(room.n, affordableModels(w, t.cost, est));
  if (n < minMuster(est)) return null;
  spend(w.res, scaleCost(t.cost, n));
  const c = createCompany(w, typeIdx, Team.Player, x, y);
  const made = fillCompany(w, c, n, x, y);
  payMusterBinding(w, c, made);
  pushLog(
    w.events,
    made >= est
      ? `${c.name} musters — ${t.name}.`
      : `${c.name} musters — ${t.name}, ${made} of ${est}. The rest of the roll is empty.`,
    LogTone.Good,
  );
  return c;
}

/**
 * What the muster costs this people beyond coin, paid once the men exist.
 *
 * Here rather than in `applyCommand` for the reason `reinforceCompany`'s refusal
 * is where it is: `recruitCompany` is the single door a player company comes
 * through, and the campaign muster and every debug path reach it without going
 * near `Cmd.Recruit`. A binding charged at the command would be a binding the
 * bots and the AI never paid.
 *
 * Charged against `made` rather than `size`, because `fillCompany` can come up
 * short when the unit store is full, and a Court that paid four thralls for
 * three men would quietly leak its whole muster over a long run.
 */
function payMusterBinding(w: World, c: Company, made: number): void {
  switch (race(w.raceId).binding) {
    case 'names':
      /**
       * The name goes onto the banner and stays there. It is not returned when
       * the company stands down and it is not returned when the company is
       * wiped — "destroyed with the company that carried it" is the passive's
       * own wording, and a name that came back on disband would make disbanding
       * a free way to re-roll a company's unit type, which is the opposite of a
       * binding.
       */
      w.consecratedNames = Math.max(0, w.consecratedNames - 1);
      c.name = `${c.name}, Named`;
      break;
    case 'thralls':
      w.thralls = Math.max(0, w.thralls - made);
      break;
    /**
     * The Splitmoot does not recruit; it releases. Every model in the company is
     * a goblin taken *out* of the warren, so a muster is the one thing that
     * relieves the press — and it is the loop the whole binding turns on: the
     * warren breeds without a ceiling, the press pulls your own buildings down,
     * and the answer is to arm it and point it at somebody.
     *
     * `w.units.kill` rather than `killUnit`, deliberately. Nobody has died: a
     * villager who picks up a spear is not a casualty, should not be mourned by
     * the men around him, should not book a loss and should not stain the
     * ground. He is simply no longer in the warren.
     */
    case 'moot':
      releaseFromWarren(w, made);
      break;
    default:
      break;
  }
}

/** Take `n` goblins off the warren, furthest from the Hearth first. */
function releaseFromWarren(w: World, n: number): void {
  if (n <= 0) return;
  const u = w.units;
  let taken = 0;
  for (let i = 0; i < u.slots.high && taken < n; i++) {
    if (u.slots.alive[i] === 0) continue;
    if (u.team[i] !== Team.Player) continue;
    if ((u.flags[i] & UF.Villager) === 0) continue;
    u.kill(i);
    taken++;
  }
  if (taken > 0) w.dirty.jobs = true;
}

/**
 * Why this company cannot be topped up, or '' if it can.
 *
 * Exported for the same reason `musterRoom` is: the panel must be able to grey
 * the button *and say why*, and "no replacements, ever" is a rule a player has
 * to be told rather than left to infer from a button that does nothing.
 */
export function reinforceError(w: World, c: Company): string {
  if (race(w.raceId).binding === 'irreplaceable') {
    return 'There will not be another. Raise a new company or fight with these.';
  }
  if (c.maxSize - c.members.length <= 0) return 'At full strength.';
  return '';
}

export function reinforceCompany(w: World, c: Company): boolean {
  const t = unitType(c.typeIdx);
  const missing = c.maxSize - c.members.length;
  if (missing <= 0) return false;
  /**
   * The Silver Reaches may not reinforce, and this is the whole of it.
   *
   * The second binding to be built, and it is deliberately the cheapest one in
   * the roster — a single refusal in the one place replacements enter the world.
   * That is what makes it a *shape* rather than a number: every other people
   * answers a thinned company with coin, and this one cannot answer it at all.
   * A gap in a Silver company is permanent until the company is disbanded, so
   * the decision it forces is "is this company still worth keeping" rather than
   * "can I afford the replacements", which is a different question and the only
   * one they ever get to ask.
   *
   * Guarded here rather than at `Cmd.Reinforce` on purpose: this is the single
   * door replacements come through, and `stepCompanies` and the campaign muster
   * both reach it without going near the command. A guard at the command would
   * have been a guard on the player and not on the rule.
   */
  if (race(w.raceId).binding === 'irreplaceable') return false;
  /**
   * The fifteen per cent off is a *replacement* rate, not a discount on men.
   *
   * It was safe while a company was raised whole or not at all. Now that a
   * muster takes what it can pay for, "raise the Sixth one man strong and
   * reinforce it to forty" would have bought a regiment at 0.85 — a fifteen per
   * cent rebate for nothing but two clicks, and the bots would have found it by
   * accident because they reinforce before they recruit. So the cheap rate is
   * capped at the casualties this company has actually taken; anybody filling an
   * empty roll pays list price for them.
   */
  const replacing = Math.min(missing, c.losses);
  const cost = scaleCost(t.cost, replacing, 0.85 * w.mods.reinforceCost);
  const rest = scaleCost(t.cost, missing - replacing, w.mods.reinforceCost);
  for (const k of Object.keys(rest)) cost[k] = (cost[k] ?? 0) + rest[k];
  if (!canAfford(w.res, cost)) return false;
  spend(w.res, cost);
  fillCompany(w, c, missing, c.ax, c.ay);
  pushLog(w.events, `${c.name} takes on ${missing} replacements.`, LogTone.Neutral);
  return true;
}

/**
 * A price for `count` men rather than for a whole establishment.
 *
 * Exported so the muster button can quote what a *short* company will actually
 * cost. It was internal while a muster was indivisible; now that
 * `recruitCompany` charges per man, the panel that offers the muster has to be
 * able to price the same thing the simulation is about to charge for.
 */
export function scaleCost(base: Record<string, number | undefined>, count: number, mul = 1) {
  const out: Record<string, number> = {};
  for (const k of Object.keys(base)) {
    const v = base[k] ?? 0;
    if (v) out[k] = v * count * mul;
  }
  return out;
}

export function stepCompanies(w: World, dt: number): void {
  const u = w.units;
  beginCrossingRoutes(w);
  // Anchors move every tick, so the grid is rebuilt every tick. It is a walk of
  // the company list, not of the population, and it replaces a quadratic.
  if (w.battle) anchorGrid.build(w);
  // Hoisted: `armies()` builds a small array, and `lanedFoe` is asked once per
  // company per tick. The banners on a field do not change during a tick.
  const sides = w.battle ? armies(w.battle) : null;

  for (let ci = 0; ci < w.companies.length; ci++) {
    const c = w.companies[ci];

    // Strength as it stood a moment ago, before the fallen are compacted out.
    // The Second Wind raises a fraction of *this*, not of the establishment: a
    // company already down to two men should not come back seven strong.
    const stood = c.members.length;

    // Compact out the fallen.
    let live = 0;
    for (let k = 0; k < c.members.length; k++) {
      const i = u.resolve(c.members[k]);
      if (i >= 0) {
        c.members[live++] = c.members[k];
        u.slotIdx[i] = live - 1;
      }
    }
    c.members.length = live;

    if (live === 0) {
      if (!c.dead) {
        /**
         * The Second Wind, which until now was a relic that did nothing.
         *
         * `mods.secondWind` is the *fraction* of the company that gets back up,
         * not a probability — the card says "half of it gets back up", and half
         * is what it means. Once per company per Siege, so a line cannot be
         * farmed back into existence, and the flag resets in `beginSiege`.
         */
        const back =
          c.team === Team.Player && !w.battle?.pvp && w.mods.secondWind > 0 && !c.usedSecondWind
            ? Math.min(c.maxSize, Math.max(1, Math.round(stood * w.mods.secondWind)))
            : 0;
        if (back > 0) {
          c.usedSecondWind = true;
          const made = fillCompany(w, c, back, c.ax, c.ay);
          if (made > 0) {
            c.lastLossTick = w.tick;
            pushEvent(w.events, EV.Levelup, c.ax, c.ay, 1, 0, c.team);
            pushLog(w.events, `${c.name} gets back up. Not all of them. Enough.`, LogTone.Good);
            continue;
          }
        }
        c.dead = true;
        // On a shared field both losses are somebody's, and the world's own
        // morale belongs to neither: it is the settlement's number.
        if (commanded(w, c)) {
          pushLog(w.events, `${c.name} is gone. All of it.`, LogTone.Dire);
          if (!w.battle?.pvp) w.morale -= 0.06;
          /**
           * And the name with it. Nothing is deducted here — the name left the
           * shelf when the company mustered and there is no path that puts it
           * back — but a binding whose cost is invisible is a binding the player
           * never learns, so the loss is said out loud where it is felt.
           */
          if (race(w.raceId).binding === 'names') {
            pushLog(
              w.events,
              'The name goes with them. It will have to be said again, from the beginning.',
              LogTone.Dire,
            );
          }
        }
      }
      continue;
    }

    // Veterancy.
    let rank = 0;
    for (let r = RANK_XP.length - 1; r >= 0; r--) {
      if (c.xp >= RANK_XP[r]) {
        rank = r;
        break;
      }
    }
    if (rank > c.rank) {
      c.rank = rank;
      if (commanded(w, c)) {
        pushEvent(w.events, EV.Levelup, c.ax, c.ay, rank, 0, c.team);
        pushLog(w.events, `${c.name} is ${RANK_NAMES[rank]} now. They have earned it badly.`, LogTone.Good);
      }
    }

    // Anchor: a commanded company marches to its order; everything else
    // follows the crowd. On a shared field both sides are commanded.
    if (commanded(w, c)) {
      const t = unitType(c.typeIdx);
      const spd = t.speed * 0.92;
      const dx = c.orderX - c.ax;
      const dy = c.orderY - c.ay;
      const d = Math.hypot(dx, dy);
      if (d > 0.08 && c.orderKind !== OrderKind.None) {
        /**
         * The banner walks its route if it has one and straight at the order if
         * it has not — which is every order across open ground, unchanged.
         *
         * `marchTarget` consumes waypoints as they are reached, so the test
         * above stays "am I at the order" rather than "am I at the end of a
         * list": a route that runs out beside a wall which has since come down
         * still ends at the place the player pointed.
         */
        const leg = marchTarget(w, c);
        const lx = leg.x - c.ax;
        const ly = leg.y - c.ay;
        const ld = Math.hypot(lx, ly) || 1;
        const step = Math.min(ld, spd * dt);
        c.ax += (lx / ld) * step;
        c.ay += (ly / ld) * step;
        c.facing = rotateToward(c.facing, Math.atan2(ly, lx), 3.4 * dt);

        /**
         * A route goes stale when the ground under it changes, and on a siege
         * field the ground changes constantly: the gate it was laid through
         * falls, and a shorter way opens two sections along. Re-laid on a slow
         * cadence, phased by company id so a whole army does not re-path on one
         * tick — a pure function of the tick and the id, so two runs of a seed
         * re-path at exactly the same moments.
         */
        if (c.route.length > 0 && w.tick % ROUTE_REFRESH === c.id % ROUTE_REFRESH) {
          planCompanyRoute(w, c);
        }
      } else if (c.orderKind !== OrderKind.None) {
        c.orderKind = OrderKind.None;
        c.route.length = 0;
      }
      // A dragged frontage names how to form up at the destination. Marching
      // still faces each route leg; the requested frontage is restored on arrival.
      if (c.orderKind === OrderKind.None && c.orderFacing != null) {
        c.facing = rotateToward(c.facing, c.orderFacing, 3.4 * dt);
        if (Math.abs(wrapAngle(c.orderFacing - c.facing)) < .0001) c.orderFacing = null;
      }

      /**
       * The leash, which until now was given only to companies nobody was
       * commanding.
       *
       * Its own note says it: *never let the banner outrun the men*. That is
       * every bit as true of a company the player has pointed at a gate — the
       * banner sails through the stone, the formation slots stream off inside
       * the town, and every man not yet in contact walks at the wall beside the
       * hole instead of queueing at it. An order across open ground never feels
       * the tether, because the banner is deliberately *slower* than the men it
       * leads and the lead only opens when they are held up.
       */
      leashAnchor(w, c);
    } else {
      /**
       * Invader companies march as companies.
       *
       * This used to be one line — set the anchor to the centroid of whoever
       * was left — which meant an attacking company had no shape at all. Its
       * models each independently sampled the flow field and converged on the
       * same tile, so an army read as a mob pouring toward a point rather than
       * a line advancing on a frontage. It also only worked *by accident*:
       * models were spawned scattered off their slots, and it was that scatter
       * that pushed them onto the long-range navigation branch. Spawn them
       * neatly in formation, as they should be, and the horde stood still.
       *
       * So the anchor is now integrated rather than derived: it follows an
       * explicit order if it has one (battle mode gives every company a lane)
       * and otherwise walks the flow field, which keeps the "walls are passable
       * at a price" routing intact — just resolved once per company instead of
       * once per man.
       */
      let sx = 0;
      let sy = 0;
      let routing = 0;
      for (const h of c.members) {
        const i = u.resolve(h);
        if (i >= 0) {
          sx += u.x[i];
          sy += u.y[i];
          if (u.state[i] === UnitState.Routing) routing++;
        }
      }
      const cxm = sx / live;
      const cym = sy / live;

      if (c.ax === 0 && c.ay === 0) {
        c.ax = cxm;
        c.ay = cym;
      }

      /**
       * Deployment is a truce, and the anchor has to honour it too.
       *
       * `step.ts` already gates targeting, swings and morale while a battle is
       * deploying, and `stepMovement` stops individual invaders navigating. But
       * the company *anchor* marched regardless — it walked its lane or sampled
       * the flow field, the formation slots went with it, and every model
       * dutifully followed its slot. So the enemy line still crossed the field
       * before the player had touched anything, which is precisely the thing
       * the truce exists to prevent. An army waits for the horn.
       */
      const deploying = w.battle?.deploying === true;

      // A company that is mostly running has stopped being a company.
      if (!deploying && routing <= live * 0.5) {
        const t = unitType(c.typeIdx);
        const spd = t.speed * 0.9;
        let mx = 0;
        let my = 0;

        if (c.orderKind !== OrderKind.None) {
          const dx = c.orderX - c.ax;
          const dy = c.orderY - c.ay;
          const d = Math.hypot(dx, dy);
          if (d > 0.15) {
            mx = dx / d;
            my = dy / d;
          } else {
            c.orderKind = OrderKind.None;
          }
        } else if (w.battle) {
          /**
           * A battle has no Hearth, so it has no flow field to walk: `flowMain`
           * is built toward hearth goals that a battle world does not have, and
           * `sample` simply fails. Left there, a company that finished its lane
           * stood still forever — both lines halted out of contact, resolve
           * froze above the break point, and the battle could never reach a
           * decision. Off the field, close on an enemy company — but **on the
           * one in your own lane, not the globally nearest**. Nearest was the
           * mob generator NEXT.md #5 describes: the player masses centre, every
           * AI company's nearest foe is the same company, and a line that
           * advanced on a frontage collapses to a point the moment the first
           * order completes. Lateral distance is weighted heavily over forward
           * distance (the armies face each other across y, so a lane is an x),
           * which keeps each company advancing on whatever stands in front of
           * *it*, and only wheeling inward when its own lane is empty.
           */
          const foe = lanedFoe(w, c, sides) ?? anchorGrid.nearestEnemy(w, c);
          if (foe) {
            const dx = foe.ax - c.ax;
            const dy = foe.ay - c.ay;
            const d = Math.hypot(dx, dy);
            if (d > CONTACT) {
              mx = dx / d;
              my = dy / d;
              /**
               * And then the ground gets a say.
               *
               * Ground the enemy cannot read is ground that only punishes the
               * player: a channel of deep water laid between the armies would
               * have drowned the machine and nobody else, and a ford nobody
               * marched to is scenery. `steerOverGround` bends this heading —
               * never more than sixty degrees, and never at all on a field
               * with no relief on it, which keeps every world that predates
               * the generator on exactly the line it was on.
               *
               * A missile company is given an appetite for height, so it will
               * take the longer way round to fight from a crest; a line of
               * spears is not, because a charge that goes round a hill to
               * avoid the climb is not a charge.
               */
              const climb = (unitType(c.typeIdx).flags & UF.Ranged) !== 0 ? 0.22 : 0;
              steerOverGround(w, c.ax, c.ay, mx, my, climb, groundSteer);
              mx = groundSteer.x;
              my = groundSteer.y;
            }
          }
        } else {
          const field = c.flowKind === 1 ? w.flowEcon : w.flowMain;
          if (field.sample(c.ax, c.ay, anchorFlow)) {
            mx = anchorFlow.x;
            my = anchorFlow.y;
          }
        }

        if (mx !== 0 || my !== 0) {
          c.ax += mx * spd * dt;
          c.ay += my * spd * dt;
          c.facing = rotateToward(c.facing, Math.atan2(my, mx), 2.6 * dt);
        }
      }

      // Never let the banner outrun the men — see the note at `leashAnchor`.
      leashTo(c, cxm, cym);
    }

    // Formation slots.
    const t = unitType(c.typeIdx);
    const spacing = spacingFor(t.radius) * (c.formation === Formation.Loose ? 1.75 : 1);
    const cos = Math.cos(c.facing);
    const sin = Math.sin(c.facing);
    let cohesion = 0;

    for (let k = 0; k < c.members.length; k++) {
      const i = u.resolve(c.members[k]);
      if (i < 0) continue;
      /**
       * A man on the wall has a post, and his post *is* his slot.
       *
       * Left to the formation, the line would try to reform on open ground and
       * every garrisoned model would be given a goal off the parapet — which
       * `stepMovement` would then dutifully steer toward, marching the garrison
       * off its own wall the instant it was put on it. He counts as cohesive
       * because he is standing exactly where he was told to.
       */
      if (u.wallTile[i] >= 0) {
        cohesion++;
        continue;
      }
      slotOffsetInto(c.formation, k, c.maxSize, spacing, c.formationWidth, slotScratch);
      // Local +x is forward, +y is right; rotate onto the field.
      const sxp = c.ax + slotScratch.x * cos - slotScratch.y * sin;
      const syp = c.ay + slotScratch.x * sin + slotScratch.y * cos;

      const engaged = u.targetH[i] >= 0 || u.targetBuilding[i] >= 0 || u.targetWall[i] >= 0;
      if (!engaged && u.state[i] !== UnitState.Routing) {
        const transit = gateTransitTarget(w, c, i, sxp, syp, gateScratch);
        u.goalX[i] = transit ? gateScratch.x : sxp;
        u.goalY[i] = transit ? gateScratch.y : syp;
      }
      const d2 = (u.x[i] - sxp) ** 2 + (u.y[i] - syp) ** 2;
      if (d2 < 4) cohesion++;

      // Shieldwall braces: slower, but the shield always faces the line's front.
      if (c.formation === Formation.Shieldwall && !engaged) {
        u.facing[i] = c.facing;
      }
    }
    c.cohesion = live > 0 ? cohesion / live : 0;

    // Retreat & rally: pull back, then recover morale fast.
    if (c.stance === Stance.Rally) {
      for (const h of c.members) {
        const i = u.resolve(h);
        if (i >= 0) u.rallyT[i] = Math.max(u.rallyT[i], 0.4);
      }
    }

    // Ashen Crown banner discipline.
    if (c.team === Team.Player && w.faction.id === 'ashen' && c.members.length > c.maxSize * 0.5) {
      for (const h of c.members) {
        const i = u.resolve(h);
        if (i >= 0 && u.morale[i] < 0.45) u.morale[i] = Math.min(0.5, u.morale[i] + 0.5 * dt);
      }
    }
  }

  // Retire wiped invader companies so the list does not grow without bound.
  //
  // Never in a battle. `BattleArmy.companies` is a list of ids and every
  // resolve site skips ids it cannot find, so a retired company leaves the
  // *denominator* of `armyResolve` rather than contributing zero to it. Wiping
  // six of an enemy's seven companies used to push its resolve *up* — the army
  // could not be broken by killing it, which is the one thing the mode is
  // about.
  if (w.tick % 90 === 0 && !w.battle) {
    // The player's own dead companies stay in the list — the roster still lists
    // them and a disband is a decision, not a sweep. Everything else, including
    // a 2v2 ally's, is retired.
    w.companies = w.companies.filter((c) => !(c.dead && !alliedWith(c.team, Team.Player)));
    invalidateCompanyIndex(w);
  }
}

export function spacingFor(radius: number): number {
  return Math.max(0.62, radius * 2.35);
}

/**
 * Formation slot in company-local space. +x forward, +y right.
 * `widthOverride` comes from a drag order — the player pulling a line out wide
 * to overlap a flank, or bunching it deep to punch through one.
 */
export function slotOffset(
  formation: number,
  idx: number,
  count: number,
  spacing: number,
  widthOverride = 0,
): { x: number; y: number } {
  const out = { x: 0, y: 0 };
  slotOffsetInto(formation, idx, count, spacing, widthOverride, out);
  return out;
}

/**
 * The same, written into a caller-owned object.
 *
 * `stepCompanies` asks for one of these per model per tick — at city scale that
 * is thirty thousand short-lived objects a tick, purely to carry two numbers a
 * few lines. The allocating form is kept for the handful of callers that want a
 * value; the hot loop uses this one.
 */
export function slotOffsetInto(
  formation: number,
  idx: number,
  count: number,
  spacing: number,
  widthOverride: number,
  out: { x: number; y: number },
): void {
  if (widthOverride > 0 && formation !== Formation.Wedge) {
    const width = Math.max(1, Math.round(widthOverride));
    const row = Math.floor(idx / width);
    const col = idx % width;
    const stagger = formation === Formation.Loose && (row & 1) === 1 ? spacing * 0.5 : 0;
    const depth = formation === Formation.Shieldwall ? 0.72 : 0.95;
    out.x = -row * spacing * depth;
    out.y = (col - (width - 1) / 2) * spacing + stagger;
    return;
  }
  switch (formation) {
    case Formation.Wedge: {
      // Rows of 1, 2, 3, … with the point forward.
      let row = 0;
      let remaining = idx;
      while (remaining > row) {
        remaining -= row + 1;
        row++;
      }
      const inRow = remaining;
      out.x = -row * spacing * 0.9;
      out.y = (inRow - row / 2) * spacing;
      return;
    }
    case Formation.Shieldwall: {
      // Three deep was the whole rule while a company was twelve men. At forty
      // it would have been a thirteen-wide ribbon, and at a hundred a fence:
      // a shieldwall is the *dense* formation and has to get deeper as it gets
      // bigger, not merely longer. Three is the floor, so a small company stands
      // exactly as it always did.
      const rows = Math.max(3, Math.round(Math.sqrt(count / 1.5)));
      const width = Math.max(1, Math.ceil(count / rows));
      const row = Math.floor(idx / width);
      const col = idx % width;
      out.x = -row * spacing * 0.72;
      out.y = (col - (width - 1) / 2) * spacing * 0.82;
      return;
    }
    case Formation.Loose: {
      const width = Math.max(1, Math.ceil(Math.sqrt(count * 1.4)));
      const row = Math.floor(idx / width);
      const col = idx % width;
      const stagger = (row & 1) === 1 ? spacing * 0.5 : 0;
      out.x = -row * spacing;
      out.y = (col - (width - 1) / 2) * spacing + stagger;
      return;
    }
    case Formation.Line:
    default: {
      /**
       * Ranks, from the size of the company rather than from a constant.
       *
       * Two ranks is right for twelve men and absurd for forty: it lays them out
       * twenty across and two deep, which is a string of men and not a block —
       * and the whole point of the establishments this pass raised is that a
       * company should read as a regiment from the camera. The rule holds the
       * frontage-to-depth ratio near 3:1, which is about where a Total War line
       * sits, and it deliberately reproduces the old constant exactly for
       * anything eight strong or under (one rank) and for a company of twelve
       * (two ranks of six). 16 → 3 ranks, 40 → 4, 100 → 7.
       */
      const rows = count > 8 ? clamp(Math.round(Math.sqrt(count / 2.2)), 2, 12) : 1;
      const width = Math.max(1, Math.ceil(count / rows));
      const row = Math.floor(idx / width);
      const col = idx % width;
      out.x = -row * spacing * 0.95;
      out.y = (col - (width - 1) / 2) * spacing;
    }
  }
}

/**
 * How far apart the kept waypoints of a route stand, in tiles.
 *
 * A route off `findPath` is one tile a step, which is a hundred and fifty
 * entries for a march across a town — far more shape than a banner needs, and a
 * hundred and fifty numbers in the hash and the save for every company under
 * orders. Every fourth tile keeps every corner that matters at this scale: the
 * banner is a point that nothing collides with, so a waypoint's job is to say
 * *which way round*, not to trace the kerb.
 */
const ROUTE_STRIDE = 4;

/** How close the banner gets to a waypoint before it takes the next one. */
const WAYPOINT_REACHED = 1.2;

/**
 * Lay a route round whatever is in the way, or leave the company to march
 * straight at its order.
 *
 * Exported because the same question is asked of a company that is *already*
 * marching — see `stepCompanies` — and asking it in two shapes is how two
 * answers start to disagree.
 */
export function planCompanyRoute(w: World, c: Company): void {
  c.route.length = 0;
  const ax = Math.floor(c.ax);
  const ay = Math.floor(c.ay);
  const ox = Math.floor(c.orderX);
  const oy = Math.floor(c.orderY);
  if (ax === ox && ay === oy) return;
  if (formationLineClear(w, c.ax, c.ay, c.orderX, c.orderY, c.team) && !crossesWater(w, c.ax, c.ay, c.orderX, c.orderY)) return;

  const tiles: number[] = [];
  /**
   * Walls are priced rather than refused — see `passable` in `path.ts`.
   *
   * This is the whole of the "impassable ring" fault. A closed circuit that the
   * search calls impassable comes back as *no route*, every caller of no-route
   * in this game falls back to steering straight at the goal, and the company
   * walks into the stone and stays there. Priced, the route always exists: it
   * prefers the gate, then a breach, then the thinnest section, and it does so
   * without anything in this file knowing what a gate is.
   */
  const n = findCrossingPath(w, ax, ay, ox, oy, c.team, tiles);
  if (n === 0) return;
  const bridge = tiles.some(tile => w.map.terrain[tile] === Terrain.Bridge);
  let previous = 0;
  for (let i = 1; i < n - 1; i++) {
    const tile = tiles[i], next = tiles[i + 1], last = tiles[previous];
    const corner = !formationLineClear(w, last % MAP_W + 0.5, Math.floor(last / MAP_W) + 0.5, next % MAP_W + 0.5, Math.floor(next / MAP_W) + 0.5, c.team, bridge);
    if (corner || i - previous >= ROUTE_STRIDE || w.map.wall[tile] === WallKind.Gate || w.map.terrain[tile] === Terrain.Bridge) {
      c.route.push(tile);
      previous = i;
    }
  }
  c.route.push(tiles[n - 1]);
}

/**
 * Where the banner is walking *next*, which is the head of its route when it
 * has one and the order itself when it has not.
 *
 * Consumes waypoints as it reaches them, so a company that has arrived is a
 * company with an empty route, and the "have I finished" test downstream stays
 * the one it has always been: distance to the order.
 */
function marchTarget(w: World, c: Company): { x: number; y: number } {
  const bridge = c.route.some(tile => w.map.terrain[tile] === Terrain.Bridge);
  while (c.route.length > 0) {
    const k = c.route[0];
    const x = (k % w.map.w) + 0.5;
    const y = Math.floor(k / w.map.w) + 0.5;
    const next = c.route[1];
    const cutsCorner = next !== undefined && !formationLineClear(w, c.ax, c.ay, next % MAP_W + 0.5, Math.floor(next / MAP_W) + 0.5, c.team, bridge);
    const reach = cutsCorner || w.map.wall[k] === WallKind.Gate || w.map.terrain[k] === Terrain.Bridge ? 0.18 : WAYPOINT_REACHED;
    if ((c.ax - x) ** 2 + (c.ay - y) ** 2 > reach * reach) return { x, y };
    c.route.shift();
  }
  return { x: c.orderX, y: c.orderY };
}

export function orderCompany(w: World, c: Company, x: number, y: number, attackMove: boolean): void {
  c.orderX = clamp(x, 1, w.map.w - 2);
  c.orderY = clamp(y, 1, w.map.h - 2);
  c.orderKind = attackMove ? OrderKind.AttackMove : OrderKind.Move;
  c.orderFacing = null;
  if (attackMove && c.stance === Stance.Hold) c.stance = Stance.Guard;
  planCompanyRoute(w, c);
}

export function companyStrength(w: World, c: Company): number {
  const u = w.units;
  let hp = 0;
  let max = 0;
  for (const h of c.members) {
    const i = u.resolve(h);
    if (i < 0) continue;
    hp += u.hp[i];
    max += u.maxHp[i];
  }
  return max > 0 ? hp / max : 0;
}
